import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import './AboutVideo.css';

const FALLBACK_EMBED_URL = 'https://www.youtube.com/embed/wuTsga_xCpw';

const extractYoutubeId = (value = '') => {
    if (!value) return '';

    try {
        const parsedUrl = new URL(value);
        const host = parsedUrl.hostname.toLowerCase();

        if (host.includes('youtu.be')) {
            return parsedUrl.pathname.replace('/', '').trim();
        }

        if (host.includes('youtube.com')) {
            if (parsedUrl.pathname.startsWith('/embed/')) {
                return parsedUrl.pathname.replace('/embed/', '').split('/')[0];
            }
            return parsedUrl.searchParams.get('v') || '';
        }
    } catch {
        const match = value.match(/(?:youtu\.be\/|youtube\.com\/embed\/|[?&]v=)([A-Za-z0-9_-]+)/);
        return match?.[1] || '';
    }

    return '';
};

const normalizeYoutubeVideo = (payload) => {
    if (!payload) return null;

    const item = Array.isArray(payload)
        ? payload[0]
        : Array.isArray(payload?.results)
            ? payload.results[0]
            : payload;

    if (!item) return null;

    const videoId = item.video_id || extractYoutubeId(item.url || '') || extractYoutubeId(item.embed_url || '');
    const embedUrl = item.embed_url || (videoId ? `https://www.youtube.com/embed/${videoId}` : '');

    if (!embedUrl) return null;

    return {
        ...item,
        video_id: videoId,
        embed_url: embedUrl
    };
};

const AboutVideo = () => {
    const { t, i18n } = useTranslation();
    const [video, setVideo] = useState(null);
    const baseUrl = useMemo(
        () => import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, ''),
        []
    );

    useEffect(() => {
        const controller = new AbortController();

        if (!baseUrl) {
            console.error('Missing VITE_API_BASE_URL');
            return () => controller.abort();
        }

        const loadYoutubeVideo = async () => {
            try {
                const response = await fetch(`${baseUrl}/api/v1/videos/youtube`, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error(`YouTube video request failed: ${response.status}`);
                }

                const data = await response.json();
                setVideo(normalizeYoutubeVideo(data));
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error(error.message || 'Failed to load YouTube video');
                setVideo(null);
            }
        };

        loadYoutubeVideo();

        return () => controller.abort();
    }, [baseUrl]);

    const videoTitle = useMemo(() => {
        if (!video) return 'About Podiatech';
        if (i18n.language === 'uz' && video.title_uz) return video.title_uz;
        if (i18n.language === 'en' && video.title_en) return video.title_en;
        return video.title || video.title_en || video.title_uz || 'About Podiatech';
    }, [i18n.language, video]);

    const videoEmbedUrl = video?.embed_url || FALLBACK_EMBED_URL;

    return (
        <section className="about-video" id="about-video">
            <div className="about-video__container">
                <motion.div
                    className="about-video__header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="about-video__title">{t('about_video.title')}</h2>
                    <p className="about-video__subtitle">{t('about_video.subtitle')}</p>
                </motion.div>

                <motion.div
                    className="about-video__wrapper"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="about-video__player">
                        <iframe
                            src={videoEmbedUrl}
                            title={videoTitle}
                            loading="lazy"
                            fetchpriority="low"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default AboutVideo;
